/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef, useState } from "react";

import styled from "styled-components";

import { XButton } from "src/components/UI/Buttons/XButton";
import { BLOCK_SHORTCUTS, GENERAL_SHORTCUTS } from "src/lib/constants";
import useIsomorphicLayoutEffect from "src/lib/hooks/useIsomorphicLayoutEffect";

const SHORTCUTS = [
  { category: "General Shortcuts", shortcuts: GENERAL_SHORTCUTS },
  { category: "Block Shortcuts", shortcuts: BLOCK_SHORTCUTS },
];

export const ShortcutsMenu = ({ fixedTop, close }: { fixedTop: number; close: () => void }) => {
  const shortcutsMenu = useRef<HTMLDivElement>(null);
  const [shorten, setShorten] = useState<boolean>(false);
  const [selected, setSelected] = useState<{ command: string; label: string; imageUrl?: string } | null>(null);

  useIsomorphicLayoutEffect(() => {
    if ((shortcutsMenu.current as HTMLElement).getBoundingClientRect().top < fixedTop) setShorten(true);
  });

  return (
    <ShortcutsMenuWrapper ref={shortcutsMenu} fixedTop={fixedTop} shorten={shorten}>
      <Header>
        Keyboard Shortcuts
        <XButton onClick={close} />
      </Header>
      <ShortcutGIFWrapper>
        {selected?.imageUrl ? (
          <img src={selected.imageUrl} style={{ height: "100%", width: "100%" }} alt="shortcut gif" />
        ) : (
          <p style={{ padding: 10, textAlign: "center" }}>
            {selected === null ? "hover over command to see use example" : selected.label}
          </p>
        )}
      </ShortcutGIFWrapper>
      <ItemWrapper>
        {SHORTCUTS.map((section) => (
          <Section key={section.category}>
            <p>{section.category}</p>
            {section.shortcuts.map((shortcut) => (
              <Item
                key={shortcut.label}
                onMouseEnter={() => setSelected(shortcut)}
                onMouseLeave={() => setSelected(null)}
              >
                <p>{shortcut.command}</p>
                <p>{shortcut.label}</p>
              </Item>
            ))}
          </Section>
        ))}
      </ItemWrapper>
    </ShortcutsMenuWrapper>
  );
};

const ShortcutGIFWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: #aaaaaa;
  width: calc(100% - 40px);
  aspect-ratio: 1.618;
  margin: 20px;
  border: 1px solid #aaaaaa;
`;

const ShortcutsMenuWrapper = styled.div<{ fixedTop: number; shorten: boolean }>`
  position: fixed;
  bottom: 64px;
  right: 55px;
  width: 375px;
  top: ${({ fixedTop, shorten }) => (shorten ? `${fixedTop}px` : "none")};
  max-height: 745px;
  backdrop-filter: blur(10px);
  user-select: none;
  font-family: "Inter";
  background: ${({ theme }) => `${theme.blurMenu.background}`};
  border: ${({ theme }) => `1px solid ${theme.blurMenu.border}`};
  color: ${({ theme }) => `${theme.blurMenu.text}`};
  overflow-y: scroll;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 20px;
  font-size: 20px;
  font-weight: 600;
`;

const ItemWrapper = styled.div`
  margin: 20px;
  bottom: 20px;
`;

const Section = styled.div`
  color: ${({ theme }) => theme.text.default};
  margin-bottom: 20px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 5px 0px;
  border-bottom: 1px solid #888888;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  color: ${({ theme }) => theme.text.subtle};
  transition: all 200ms;

  &:hover {
    color: ${({ theme }) => theme.text.default};
  }
`;
